/**
 * /ai-music-cleaner — where does this file's spectrum END, and is that end a cut?
 *
 * The restoration model (Apollo) was trained to give back what a lossy codec took
 * away. Measured 2026-10-07 on eight songs against their undamaged originals
 * (log-spectral distance, lower = closer; docs: memory
 * reference_amc_deep_effect_is_a_crossover_bug_2026_10_07):
 *
 *     input                  as is   Quick   model
 *     full band (no damage)   0.0     0.1     2.1    <- the model only moves it AWAY
 *     MP3 128 (cut 16.7 kHz)  4.2     4.2     4.3    <- nothing to gain
 *     MP3  96 (cut 15.3 kHz)  7.8     6.7     5.3
 *     MP3  64 (cut 11.3 kHz) 15.4    12.6     6.6
 *     MP3  48 (cut  7.8 kHz) 20.2    19.0     8.3
 *
 * So whether the model has anything to do is a property of the FILE, and it can be
 * read in a fraction of a second before a 40-80 MB model is downloaded and minutes
 * (hours on a CPU) are spent: the top of the spectrum is either cut off below
 * ~16 kHz, or it is not.
 *
 * A CUT is not «little treble». A dark mix rolls off gradually and keeps its noise
 * above; a codec (or a low sample rate) leaves a wall — tens of dB within a few
 * hundred hertz, digital silence behind it. Both numbers are measured here:
 *   cutoffHz — the highest frequency still carrying signal (50 dB under the
 *              1-4 kHz level);
 *   edgeDb   — how far the level falls across that point (just below it against
 *              just above it). An MP3 wall reads ~80 dB, a natural roll-off 5-15.
 *
 * Pure function over Float32Arrays — no DOM, no Web Audio — so it runs in node
 * (tests/js/amc-cutoff.test.mjs) exactly as it runs on the page.
 */
(function (root) {
    'use strict';

    var FFT = 4096;
    var HALF = FFT / 2;
    var MAX_FRAMES = 240;            // spread evenly over the file; the answer is a long-term property
    var SILENT_RMS = 1e-4;           // frames quieter than -80 dBFS say nothing about the spectrum
    var MIN_FRAMES = 8;
    var LIVE_BELOW_MID_DB = 50;      // «still carrying signal»
    var SMOOTH_BINS = 9;
    /* The model helped at a 15.3 kHz cut (MP3 96 kbps reads 15.28-15.39 kHz on
       eight songs) and gained nothing at 16.7 kHz. The gate sits just above the
       first and below the «16 kHz ceiling» class — YouTube's AAC, free-tier AI
       exports: there is nothing audible to add over 16 kHz, Quick lifts that
       sliver in seconds, and minutes of model time would buy nothing measured. */
    var RESTORABLE_BELOW_HZ = 15700;
    /* MP3 walls measured 75-95 dB, 16-bit band-limited PCM ~60; gradual roll-offs
       and hiss-covered cuts stay under 25. */
    var MIN_EDGE_DB = 40;

    var _win = new Float32Array(FFT);
    for (var wi = 0; wi < FFT; wi++) _win[wi] = 0.5 - 0.5 * Math.cos(2 * Math.PI * wi / FFT);
    var _rev = new Int32Array(FFT);
    (function () {
        var bits = Math.round(Math.log(FFT) / Math.LN2);
        for (var k = 0; k < FFT; k++) {
            var j = 0, n = k;
            for (var b = 0; b < bits; b++) { j = (j << 1) | (n & 1); n >>= 1; }
            _rev[k] = j;
        }
    })();

    function fft(re, im) {
        for (var k = 0; k < FFT; k++) {
            var j = _rev[k];
            if (j > k) { var tr = re[k]; re[k] = re[j]; re[j] = tr; var ti = im[k]; im[k] = im[j]; im[j] = ti; }
        }
        for (var len = 2; len <= FFT; len <<= 1) {
            var half = len >> 1, ang = -2 * Math.PI / len, wr = Math.cos(ang), wim = Math.sin(ang);
            for (var i = 0; i < FFT; i += len) {
                var cr = 1, ci = 0;
                for (var t = 0; t < half; t++) {
                    var a = i + t, b2 = a + half;
                    var xr = re[b2] * cr - im[b2] * ci, xi = re[b2] * ci + im[b2] * cr;
                    re[b2] = re[a] - xr; im[b2] = im[a] - xi; re[a] += xr; im[a] += xi;
                    var ncr = cr * wr - ci * wim; ci = cr * wim + ci * wr; cr = ncr;
                }
            }
        }
    }

    function meanDb(db, lo, hi) {
        var s = 0, n = 0;
        for (var k = lo; k <= hi; k++) { s += db[k]; n++; }
        return n ? s / n : NaN;
    }

    /**
     * @param {Float32Array[]} channels  PCM, one array per channel
     * @param {number} sampleRate
     * @returns {{cutoffHz:number, edgeDb:(number|null), restorable:boolean, why:string, frames:number}}
     *   why: 'cut' | 'low_rate' | 'full_band' | 'no_wall' | 'quiet'
     */
    function measure(channels, sampleRate) {
        var none = function (why, frames) { return { cutoffHz: 0, edgeDb: null, restorable: false, why: why, frames: frames || 0 }; };
        if (!channels || !channels.length || !channels[0] || !(sampleRate > 0)) return none('quiet');
        var n = channels[0].length;
        if (n < FFT) return none('quiet');

        var power = new Float64Array(HALF + 1);
        var re = new Float32Array(FFT), im = new Float32Array(FFT);
        var slots = Math.min(MAX_FRAMES, Math.floor(n / FFT));
        var span = n - FFT;
        var frames = 0;
        for (var f = 0; f < slots; f++) {
            var start = slots > 1 ? Math.floor(span * f / (slots - 1)) : 0;
            for (var c = 0; c < channels.length; c++) {
                var ch = channels[c];
                var e = 0;
                for (var i = 0; i < FFT; i++) { var v = ch[start + i]; e += v * v; re[i] = v * _win[i]; im[i] = 0; }
                if (Math.sqrt(e / FFT) < SILENT_RMS) continue;
                fft(re, im);
                for (var k = 0; k <= HALF; k++) power[k] += re[k] * re[k] + im[k] * im[k];
                frames++;
            }
        }
        if (frames < MIN_FRAMES) return none('quiet', frames);

        var raw = new Float64Array(HALF + 1);
        for (var k2 = 0; k2 <= HALF; k2++) raw[k2] = 10 * Math.log(power[k2] / frames + 1e-30) / Math.LN10;
        /* Smoothing with the edges HELD, not zero-padded: a zero in a dB array is
           0 dB — louder than any music — and the bins next to an edge would read
           as signal (the prototype of this detector called every file full-band
           that way). */
        var db = new Float64Array(HALF + 1), r = (SMOOTH_BINS - 1) >> 1;
        for (var k3 = 0; k3 <= HALF; k3++) {
            var s = 0;
            for (var d = -r; d <= r; d++) { var q = k3 + d; s += raw[q < 0 ? 0 : (q > HALF ? HALF : q)]; }
            db[k3] = s / SMOOTH_BINS;
        }

        var hz = sampleRate / FFT;
        var bin = function (freq) { return Math.max(0, Math.min(HALF, Math.round(freq / hz))); };
        var nyquist = sampleRate / 2;
        var mid = meanDb(db, bin(1000), bin(Math.min(4000, nyquist * 0.8)));
        var top = bin(Math.min(20000, nyquist - 500));
        var cut = -1;
        for (var k4 = top; k4 >= bin(500); k4--) { if (db[k4] > mid - LIVE_BELOW_MID_DB) { cut = k4; break; } }
        if (cut < 0) return none('quiet', frames);
        var cutoffHz = cut * hz;

        /* The file's own sample rate ends below the gate: everything above is
           missing by construction, there is no «above» to measure a wall against. */
        if (nyquist <= RESTORABLE_BELOW_HZ + 500) {
            return { cutoffHz: Math.round(Math.min(cutoffHz, nyquist)), edgeDb: null, restorable: true, why: 'low_rate', frames: frames };
        }
        if (cutoffHz >= RESTORABLE_BELOW_HZ) {
            return { cutoffHz: Math.round(cutoffHz), edgeDb: null, restorable: false, why: 'full_band', frames: frames };
        }
        var below = meanDb(db, bin(cutoffHz - 1500), bin(cutoffHz - 300));
        var above = meanDb(db, bin(cutoffHz + 700), Math.min(top, bin(cutoffHz + 2500)));
        var edgeDb = Math.round((below - above) * 10) / 10;
        var wall = edgeDb >= MIN_EDGE_DB;
        return { cutoffHz: Math.round(cutoffHz), edgeDb: edgeDb, restorable: wall, why: wall ? 'cut' : 'no_wall', frames: frames };
    }

    /** Same answer for an AudioBuffer (the page's form). */
    function measureBuffer(audioBuffer) {
        var chans = [];
        for (var c = 0; c < audioBuffer.numberOfChannels; c++) chans.push(audioBuffer.getChannelData(c));
        return measure(chans, audioBuffer.sampleRate);
    }

    var api = { measure: measure, measureBuffer: measureBuffer, RESTORABLE_BELOW_HZ: RESTORABLE_BELOW_HZ, MIN_EDGE_DB: MIN_EDGE_DB };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    root.AmcCutoff = api;
})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : globalThis));
